import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import Tippy from '@tippyjs/react'

const sectionShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
})

const propTypes = {
  percent: PropTypes.number.isRequired,
  completedSections: PropTypes.arrayOf(PropTypes.string).isRequired,
  sections: PropTypes.arrayOf(sectionShape).isRequired,
  variant: PropTypes.oneOf(['card', 'tab-header']),
  title: PropTypes.string,
}

const defaultProps = {
  variant: 'card',
  title: 'Profile Completion',
}

// Discrete bar colors per design (4-tab: 25/50/75/100; 5-tab: 20/40/60/80/100).
function barFillColor(percent) {
  if (!percent || percent <= 0) return 'transparent'
  if (percent >= 100) return '#97EE00'
  if (percent >= 80) return '#FFE500'
  if (percent >= 60) return '#FFBB00' // #FB0
  if (percent >= 40) return '#FF6E00'
  if (percent >= 20) return '#FF4A4A'
  return 'transparent'
}

// Render the label on two lines for legibility, keeping the "&" on the first
// line (e.g. "Staffing &" / "Instruction").
function renderLabel(label) {
  const separator = ' & '
  const index = label.indexOf(separator)
  if (index === -1) return label
  return (
    <React.Fragment>
      {label.slice(0, index + 2)}
      <br />
      {label.slice(index + 3)}
    </React.Fragment>
  )
}

function CheckIcon() {
  return (
    <svg
      className="profile-completion-bar__icon-svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="10" />
      <path
        d="M5.5 10.5l3 3 6-6"
        fill="none"
        stroke="#FFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function XIcon() {
  return (
    <svg
      className="profile-completion-bar__icon-svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="10" />
      <path
        d="M6.5 6.5l7 7M13.5 6.5l-7 7"
        fill="none"
        stroke="#FFF"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ProfileCompletionBar({
  percent,
  completedSections,
  sections,
  variant,
  title,
}) {
  return (
    <div
      className={classnames(
        'profile-completion-bar',
        `profile-completion-bar--${variant}`
      )}
    >
      <header className="profile-completion-bar__header">
        <h4 className="profile-completion-bar__title">{title}</h4>
        <span className="profile-completion-bar__percent">
          {percent}% Complete
        </span>
      </header>
      <div
        className="profile-completion-bar__track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="profile-completion-bar__fill"
          style={{
            width: `${percent}%`,
            backgroundColor: barFillColor(percent),
          }}
        />
      </div>
      <ul className="profile-completion-bar__sections">
        {sections.map(({ key, label, tooltip }) => {
          const done = completedSections.includes(key)
          const item = (
            <li
              className={classnames(
                'profile-completion-bar__section',
                done
                  ? 'profile-completion-bar__section--complete'
                  : 'profile-completion-bar__section--incomplete'
              )}
            >
              <span
                className="profile-completion-bar__icon"
                tabIndex="0"
                aria-label={`${label}: ${done ? 'complete' : 'incomplete'}`}
              >
                {done ? <CheckIcon /> : <XIcon />}
              </span>
              <span className="profile-completion-bar__label">
                {renderLabel(label)}
              </span>
            </li>
          )
          if (!tooltip) return React.cloneElement(item, { key })
          return (
            <Tippy key={key} content={tooltip}>
              {item}
            </Tippy>
          )
        })}
      </ul>
    </div>
  )
}

ProfileCompletionBar.propTypes = propTypes
ProfileCompletionBar.defaultProps = defaultProps

export default ProfileCompletionBar
